/**
 * Shared visual cues for job detail UI that reveals more on hover, focus, or tap.
 * Keep in sync with SalaryInferenceExplainer (custom tooltip) and native title= tooltips.
 */

/** Focus ring used for keyboard-accessible “more info” triggers */
export const JD_MORE_INFO_FOCUS_RING =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B5E45] dark:focus-visible:ring-[#98CFA8] rounded-sm';

/**
 * Text (or inline content) that opens a custom tooltip / tap panel (e.g. SalaryInferenceExplainer).
 */
export const JD_MORE_INFO_CUSTOM_TRIGGER = `border-b border-dotted border-current/40 cursor-help underline-offset-2 hover:border-current/70 ${JD_MORE_INFO_FOCUS_RING}`;

/**
 * Same discoverability cues as JD_MORE_INFO_CUSTOM_TRIGGER (hover/focus/tap reveals more info)
 * without the dotted underline — for compact or multi-line triggers where a full-width rule
 * would wrap raggedly. Pair with a small explicit indicator (e.g. an info dot) in the trigger's
 * own content instead. Pass via SalaryInferenceExplainer's `triggerClassName`.
 */
export const JD_MORE_INFO_CUSTOM_TRIGGER_BARE = `cursor-help ${JD_MORE_INFO_FOCUS_RING}`;

/**
 * Plain text (or compact labels) that rely on the browser `title` attribute for extra detail.
 */
export const JD_MORE_INFO_NATIVE_TITLE_TEXT = `cursor-help border-b border-dotted border-jc-accent/40 dark:border-jc-accent/50 hover:border-jc-accent/75 pb-px transition-colors ${JD_MORE_INFO_FOCUS_RING}`;

/**
 * Wrapper for icons, badges, or small images that use `title=` — hover ring signals interactivity.
 */
export const JD_MORE_INFO_NATIVE_TITLE_WRAP =
  'inline-flex cursor-help rounded-full ring-2 ring-transparent hover:ring-jc-accent/40 dark:hover:ring-jc-accent/50 focus-within:ring-jc-accent/50 transition-shadow';

/**
 * Icon-only controls that show a Tooltip on hover — subtle hover ring.
 */
export const JD_MORE_INFO_ICON_BUTTON_HOVER =
  'transition-shadow hover:shadow-md hover:ring-2 hover:ring-jc-accent/25 dark:hover:ring-jc-accent/35';

/**
 * Chart hit targets (bars, rows, segments) that open a popover.
 */
export const JD_MORE_INFO_CHART_HIT =
  'transition-[filter,opacity] opacity-[0.92] hover:opacity-100 hover:brightness-[1.06]';
